import * as React from 'react';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Collapse,
    FormControl,
    FormControlLabel,
    FormLabel,
    IconButton,
    InputAdornment,
    MenuItem,
    Radio,
    RadioGroup,
    TextField,
    Typography,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';

import { LANGUAGES, LANGUAGE_MAP } from '../../utils/lang';
import { useSignup } from '../context';
import { backFromConfirm, Details } from '../flow';
import { tenantDashboardHost } from '../host';
import { codeOptionReasonText, codeOptionText, DUPLICATE_ALLOWED_WORDS, ERROR_WORDS, errorText } from '../labels';
import type { CodeOption, Company, LogoChoice } from '../types';
import LogoPicker from './LogoPicker';

// Confirm (plan §16, §7 `account`): the company as the booking system knows it (read-only), the name,
// the dashboard address, the languages and the default one, the logo, and "Do you want your own branded mobile
// app?" -- with an app, an existing Branch.io key and secret may be pasted (optional; checked after the account is
// created; backend has-app plan §4) -- and, while sandboxes are offered, "Also create a sandbox" (ESB). A company
// already on TourOptima (D16) stops here.
//
// The dashboard address (the tenant code; code options plan 2026-10-23 §5) is picked from `derived.code_options`:
// the original first (disabled, with the reason, when it cannot be had), the free generated ones after it, and —
// when the invite allows edits — "Other", a code typed and checked live with `code_check`. An older backend without
// options: the derived code read-only, or typed and checked live when the invite allows edits.

/** The code is a DNS label (`<code>.dashboard.curatedplanet.com`); 61 characters leave room for the
 *  sandbox's `<code>sbx` (contract CreateBody.code maxLength). */
export const CODE_MAX_LEN = 61;
const CODE_RE = /^[a-z0-9]+$/;
const DEBOUNCE_MS = 400;
/** The "Other" row's radio value: never a code (codes are `[a-z0-9]`). */
const OTHER = 'other:';

export const BRANCH_BOTH_WORDS = 'Give both the key and the secret, or neither.';

/** The sandbox question (backend ESB), asked only while `GET restechs` says `sandbox_offered`. */
export const SANDBOX_WORDS = 'Also create a sandbox (a safe copy for testing; nothing is sent to guests)';

export function codeFormatProblem(code: string): string | null {
    if (!code) { return 'Choose a web address.'; }
    if (code.length > CODE_MAX_LEN) { return `At most ${CODE_MAX_LEN} characters.`; }
    if (!CODE_RE.test(code)) { return 'Use lower-case letters and digits only.'; }
    return null;
}

type CodeStatus =
    | { state: 'ok' }
    | { state: 'checking' }
    | { state: 'problem'; message: string }
    | { state: 'unknown'; message: string };

/** The chosen code's availability: a code the `account` answer says is free (the derived one, an offered option) is
 *  known; a typed one is asked with `code_check` after a pause in typing; any other is not to be had. */
function useCodeStatus(code: string, knownFree: string[], typed: boolean): CodeStatus {
    const { api, sessionLost } = useSignup();
    const [answer, setAnswer] = React.useState<{ code: string; status: CodeStatus } | null>(null);
    const formatProblem = codeFormatProblem(code);
    const known = knownFree.includes(code);

    React.useEffect(() => {
        if (!typed || formatProblem || known) { return undefined; }
        let active = true;
        const timer = setTimeout(() => {
            api.codeCheck(code).then(
                (r) => {
                    if (!active) { return; }
                    setAnswer({
                        code,
                        status: r.available ? { state: 'ok' } : { state: 'problem', message: r.problem || 'This address is not available.' },
                    });
                },
                (e) => {
                    if (!active || sessionLost(e)) { return; }
                    setAnswer({ code, status: { state: 'unknown', message: errorText(e) } });
                },
            );
        }, DEBOUNCE_MS);
        return () => {
            active = false;
            clearTimeout(timer);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [code, typed, formatProblem, known]);

    if (formatProblem) { return { state: 'problem', message: formatProblem }; }
    if (known) { return { state: 'ok' }; }
    if (!typed) { return { state: 'problem', message: ERROR_WORDS.code_unavailable }; }
    if (!answer || answer.code !== code) { return { state: 'checking' }; }
    return answer.status;
}

function countryName(iso: string): string {
    if (!iso) { return ''; }
    try {
        const names = new (Intl as any).DisplayNames(['en'], { type: 'region' });
        return names.of(iso.toUpperCase()) || iso;
    } catch (_e) {
        return iso;
    }
}

/** A code option's row: the dashboard address it gives, where it comes from and, when it cannot be had, why. */
function CodeOptionLabel({ option, vendor }: { option: CodeOption; vendor: string }) {
    const caption = codeOptionText(option.source, vendor);
    return (
        <Box component="span" sx={{ display: 'block', py: 0.5 }}>
            <Typography component="span" sx={{ display: 'block', fontFamily: 'monospace', fontSize: '1rem', overflowWrap: 'anywhere' }}>
                {tenantDashboardHost(option.code)}
            </Typography>
            {caption && (
                <Typography component="span" variant="body2" color="text.secondary" sx={{ display: 'block' }}>{caption}</Typography>
            )}
            {!option.available && (
                <Typography component="span" variant="body2" color="error" sx={{ display: 'block' }}>
                    {codeOptionReasonText(option.reason)}
                </Typography>
            )}
        </Box>
    );
}

const langName = (code: string): string => (LANGUAGE_MAP as Record<string, { name: string }>)[code]?.name || code;

function Facts({ company, vendor }: { company: Company; vendor: string }) {
    const address = Object.values(company.address || {}).filter(Boolean).join(', ');
    const rows: Array<[string, string]> = [
        ['Company', company.name],
        ['Website', company.url],
        ['E-mail', company.contact_email],
        ['Phone', company.contact_phone],
        ['Address', address],
        ['Country', countryName(company.country)],
        ['Time zone', company.timezone],
        ['Currency', company.currency],
    ].filter(([, v]) => !!v) as Array<[string, string]>;
    return (
        <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="subtitle2" gutterBottom>From {vendor}</Typography>
            <Box component="dl" sx={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', columnGap: 2, rowGap: 0.5, m: 0 }}>
                {rows.map(([k, v]) => (
                    <React.Fragment key={k}>
                        <Typography component="dt" variant="body2" color="text.secondary">{k}</Typography>
                        <Typography component="dd" variant="body2" sx={{ m: 0, overflowWrap: 'anywhere' }}>{v}</Typography>
                    </React.Fragment>
                ))}
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Something wrong here? Correct it in {vendor}; TourOptima keeps it in sync.
            </Typography>
        </Box>
    );
}

export default function Confirm() {
    const { flow, dispatch } = useSignup();
    const view = flow.view!;
    const { derived, company } = view;
    const vendor = flow.restech?.display_name || 'your booking system';
    const vendorName = flow.restech?.display_name || 'booking system';
    const start: Details = flow.details || {
        name: derived.name || company.name,
        code: derived.code,
        lang_enabled: derived.lang_enabled.length ? derived.lang_enabled : ['en'],
        default_lang: derived.lang_enabled.length === 1 ? derived.lang_enabled[0] : '',
        logo: { choice: 'auto' },
    };
    // The offered codes (none from an older backend) and the free ones among them.
    const codeOptions = derived.code_options || [];
    const picking = codeOptions.length > 0;
    let freeCodes: string[] = derived.code_available ? [derived.code] : [];
    if (picking) {
        freeCodes = codeOptions.filter((o) => o.available).map((o) => o.code);
    }
    const [name, setName] = React.useState(start.name);
    // The typed code: "Other" (or an older backend's editable field); an older backend's read-only code.
    const [code, setCode] = React.useState(view.code_editable ? start.code : derived.code);
    // The picked option, or OTHER: a code typed before, the earlier pick while it is still free, else the default.
    const [choice, setChoice] = React.useState(() => {
        if (view.code_editable && start.code_typed) { return OTHER; }
        if (freeCodes.includes(start.code)) { return start.code; }
        return freeCodes.includes(derived.code) ? derived.code : '';
    });
    const [langs, setLangs] = React.useState<string[]>(start.lang_enabled);
    const [defaultLang, setDefaultLang] = React.useState(start.default_lang);
    const [logo, setLogo] = React.useState<LogoChoice>(start.logo);
    const [hasApp, setHasApp] = React.useState(!!start.has_app);
    const [branchOpen, setBranchOpen] = React.useState(!!(start.branchio_key || start.branchio_secret));
    const [branchKey, setBranchKey] = React.useState(start.branchio_key || '');
    const [branchSecret, setBranchSecret] = React.useState(start.branchio_secret || '');
    const [showSecret, setShowSecret] = React.useState(false);
    const [sandbox, setSandbox] = React.useState(!!start.sandbox);
    const [submitted, setSubmitted] = React.useState(false);
    const flash = flow.flash;
    const [codeRefusal, setCodeRefusal] = React.useState<string | null>(flash && flash.field === 'code' ? flash.message : null);
    const general = flash && flash.field !== 'code' ? flash.message : null;

    const typed = picking ? choice === OTHER : view.code_editable;
    const chosen = picking && choice !== OTHER ? choice : code;
    const status = useCodeStatus(chosen, freeCodes, typed);
    const nameProblem = name.trim() ? null : 'Enter your company name.';
    const langsProblem = langs.length ? null : 'Choose at least one language.';
    const defaultProblem = defaultLang && langs.includes(defaultLang) ? null : 'Choose the default language.';
    const key = branchKey.trim();
    const secret = branchSecret.trim();
    // Both or neither, and only with an app (unticking the app drops them).
    const branchProblem = hasApp && !!key !== !!secret ? BRANCH_BOTH_WORDS : null;
    // A create refusal of the code holds while that code is still the chosen one (until the client edits it or picks
    // another); with a read-only code only Curated Planet can help. Fresh options after `code_taken` choose anew.
    const refusalHolds = !!codeRefusal && chosen === start.code;
    const codeBlocked = status.state === 'checking' || status.state === 'problem' || refusalHolds;

    const suggested = React.useMemo(() => {
        const first = [...derived.lang_enabled, ...derived.lang_suggestions];
        return Array.from(new Set(first));
    }, [derived]);
    const options = React.useMemo(() => {
        const rest = (LANGUAGES as Array<{ code: string; name: string }>).map((l) => l.code).filter((c) => !suggested.includes(c));
        return [...suggested, ...rest];
    }, [suggested]);

    const onLangs = (next: string[]) => {
        setLangs(next);
        if (!next.includes(defaultLang)) {
            setDefaultLang(next.length === 1 ? next[0] : '');
        }
    };

    // Already on TourOptima stops here, unless a test or demo platform allows a second account (with a warning).
    const duplicateBlocked = view.already_onboarded && !view.duplicates_allowed;

    const onSubmit = (ev: React.FormEvent) => {
        ev.preventDefault();
        setSubmitted(true);
        if (duplicateBlocked || nameProblem || langsProblem || defaultProblem || codeBlocked || branchProblem) { return; }
        const branch = hasApp && key && secret ? { branchio_key: key, branchio_secret: secret } : {};
        dispatch({
            type: 'confirmed',
            details: {
                name: name.trim(), code: chosen, code_typed: typed, lang_enabled: langs, default_lang: defaultLang, logo,
                has_app: hasApp, ...branch,
                ...(flow.sandboxOffered ? { sandbox } : {}),
            },
        });
    };

    const back = () => dispatch({ type: 'go', step: backFromConfirm(flow) });

    if (duplicateBlocked) {
        return (
            <Box>
                <Typography variant="h5" component="h1" gutterBottom>{company.name}</Typography>
                <Alert severity="warning" sx={{ my: 2 }}>{ERROR_WORDS.already_onboarded}</Alert>
                <Typography color="text.secondary" paragraph>
                    If you meant to set up a different company, go back and choose it.
                </Typography>
                <Button onClick={back}>Back</Button>
            </Box>
        );
    }

    let codeHelper: React.ReactNode;
    if (refusalHolds && !picking) {
        codeHelper = codeRefusal;
    } else if (status.state === 'problem' || status.state === 'unknown') {
        codeHelper = status.message;
    } else if (status.state === 'checking') {
        codeHelper = 'Checking…';
    } else {
        codeHelper = `Your dashboard: ${tenantDashboardHost(code)}`;
    }

    // The existing free-text field: an older backend's editable code, or the "Other" row.
    const codeField = (
        <TextField
            id="signup-code"
            label="Dashboard address"
            value={code}
            onChange={(e) => {
                setCode(e.target.value.toLowerCase().trim());
                setCodeRefusal(null);
            }}
            fullWidth
            margin="normal"
            required
            inputProps={{ spellCheck: false, autoCapitalize: 'off', maxLength: CODE_MAX_LEN, 'aria-describedby': 'signup-code-status' }}
            FormHelperTextProps={{ id: 'signup-code-status' } as object}
            error={refusalHolds || status.state === 'problem'}
            helperText={codeHelper}
        />
    );

    let codeChooser: React.ReactNode;
    if (picking) {
        const noneFree = !freeCodes.length;
        let pickProblem: string | null = null;
        if (!typed && status.state === 'problem') {
            pickProblem = noneFree && !view.code_editable ? ERROR_WORDS.code_unavailable : status.message;
        }
        codeChooser = (
            <FormControl component="fieldset" margin="normal" fullWidth>
                <FormLabel component="legend" id="signup-code-options-label">Your dashboard address</FormLabel>
                <RadioGroup
                    aria-labelledby="signup-code-options-label"
                    name="code"
                    value={choice}
                    onChange={(e) => {
                        setChoice(e.target.value);
                        setCodeRefusal(null);
                    }}
                >
                    {codeOptions.map((o) => (
                        <FormControlLabel
                            key={o.code}
                            value={o.code}
                            control={<Radio />}
                            disabled={!o.available}
                            label={<CodeOptionLabel option={o} vendor={vendorName} />}
                            sx={{ alignItems: 'flex-start', '& .MuiRadio-root': { mt: 0.25 } }}
                        />
                    ))}
                    {view.code_editable && <FormControlLabel value={OTHER} control={<Radio />} label="Other" />}
                </RadioGroup>
                {choice === OTHER && <Box sx={{ pl: 4 }}>{codeField}</Box>}
                {pickProblem && <Alert severity="error" sx={{ mt: 1 }}>{pickProblem}</Alert>}
                {codeRefusal && <Alert severity={refusalHolds ? 'error' : 'warning'} sx={{ mt: 1 }}>{codeRefusal}</Alert>}
            </FormControl>
        );
    } else if (view.code_editable) {
        codeChooser = codeField;
    } else {
        codeChooser = (
            <Box sx={{ my: 2 }}>
                <Typography variant="body2" color="text.secondary">Your dashboard address</Typography>
                <Typography data-testid="signup-code-readonly" sx={{ fontFamily: 'monospace', fontSize: '1rem' }}>
                    {tenantDashboardHost(derived.code)}
                </Typography>
                {(status.state === 'problem' || codeRefusal) && (
                    <Alert severity="error" sx={{ mt: 1 }}>{codeRefusal || (status as { message: string }).message}</Alert>
                )}
            </Box>
        );
    }

    return (
        <Box component="form" noValidate onSubmit={onSubmit} aria-label="Confirm your company">
            <Typography variant="h5" component="h1" gutterBottom>Check your company details</Typography>
            <Typography color="text.secondary" paragraph>
                This is what we found in {vendor}. Check the name, the address of your dashboard and the languages your guests
                speak.
            </Typography>
            {general && <Alert severity="error" sx={{ mb: 2 }}>{general}</Alert>}
            {view.already_onboarded && view.duplicates_allowed && (
                <Alert severity="warning" sx={{ mb: 2 }}>{DUPLICATE_ALLOWED_WORDS}</Alert>
            )}

            <TextField
                id="signup-name"
                label="Company name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                fullWidth
                margin="normal"
                required
                inputProps={{ maxLength: 1024 }}
                error={submitted && !!nameProblem}
                helperText={(submitted && nameProblem) || 'Shown to your guests.'}
            />

            {codeChooser}

            <Autocomplete
                multiple
                id="signup-languages"
                options={options}
                value={langs}
                onChange={(_e, next) => onLangs(next as string[])}
                getOptionLabel={(c) => langName(c)}
                groupBy={(c) => (suggested.includes(c) ? 'Suggested' : 'All languages')}
                filterSelectedOptions
                renderTags={(value, getTagProps) => value.map((c, index) => (
                    <Chip label={langName(c)} size="small" {...getTagProps({ index })} key={c} />
                ))}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label="Languages"
                        margin="normal"
                        error={submitted && !!langsProblem}
                        helperText={(submitted && langsProblem) || 'The languages your messages to guests are written in.'}
                    />
                )}
            />

            <TextField
                id="signup-default-lang"
                select
                label="Default language"
                value={langs.includes(defaultLang) ? defaultLang : ''}
                onChange={(e) => setDefaultLang(e.target.value)}
                fullWidth
                margin="normal"
                required
                disabled={!langs.length}
                error={submitted && !!defaultProblem}
                helperText={(submitted && defaultProblem) || 'Guests whose language is unknown or not offered get this one.'}
            >
                {langs.map((c) => (
                    <MenuItem key={c} value={c}>{langName(c)}</MenuItem>
                ))}
            </TextField>

            <Box sx={{ mt: 2 }}>
                <LogoPicker value={logo} onChange={setLogo} />
            </Box>

            <Box sx={{ mt: 3 }}>
                <FormControlLabel
                    control={<Checkbox checked={hasApp} onChange={(e) => setHasApp(e.target.checked)} />}
                    label="Do you want your own branded mobile app?"
                />
                <Typography variant="body2" color="text.secondary">
                    {hasApp
                        ? 'The steps to set it up appear in What’s next on your dashboard.'
                        : 'Your guests use our web pages and messages. You can ask for an app later.'}
                </Typography>
                <Collapse in={hasApp} unmountOnExit>
                    <Button
                        size="small"
                        sx={{ mt: 1, px: 0.5 }}
                        onClick={() => setBranchOpen((open) => !open)}
                        aria-expanded={branchOpen}
                        aria-controls="signup-branch-keys"
                    >
                        Already have Branch.io? Paste your key and secret (optional)
                    </Button>
                    <Collapse in={branchOpen}>
                        <Box id="signup-branch-keys">
                            <TextField
                                id="signup-branch-key"
                                label="Branch.io key"
                                value={branchKey}
                                onChange={(e) => setBranchKey(e.target.value)}
                                fullWidth
                                margin="normal"
                                autoComplete="off"
                                inputProps={{ spellCheck: false, autoCapitalize: 'off', maxLength: 256 }}
                                error={submitted && !!branchProblem && !key}
                                helperText={(submitted && branchProblem && !key && branchProblem) || 'Starts with key_live_.'}
                            />
                            <TextField
                                id="signup-branch-secret"
                                label="Branch.io secret"
                                type={showSecret ? 'text' : 'password'}
                                value={branchSecret}
                                onChange={(e) => setBranchSecret(e.target.value)}
                                fullWidth
                                margin="normal"
                                autoComplete="new-password"
                                inputProps={{ spellCheck: false, autoCapitalize: 'off', maxLength: 256 }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                edge="end"
                                                aria-label={showSecret ? 'Hide the secret' : 'Show the secret'}
                                                onClick={() => setShowSecret((v) => !v)}
                                            >
                                                {showSecret ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                                error={submitted && !!branchProblem && !secret}
                                helperText={
                                    (submitted && branchProblem && !secret && branchProblem)
                                    || 'We check the keys once your account exists; the secret is never shown again.'
                                }
                            />
                        </Box>
                    </Collapse>
                </Collapse>
            </Box>

            {flow.sandboxOffered && (
                <Box sx={{ mt: 3 }}>
                    <FormControlLabel
                        control={<Checkbox checked={sandbox} onChange={(e) => setSandbox(e.target.checked)} />}
                        label={SANDBOX_WORDS}
                    />
                    <Typography variant="body2" color="text.secondary">
                        {sandbox
                            ? 'We create it once your account is ready; the Onboarding page shows how it is going.'
                            : 'A place to try changes and integrations without touching your live account.'}
                    </Typography>
                </Box>
            )}

            <Facts company={company} vendor={vendor} />

            <Box sx={{ mt: 3, display: 'flex', gap: 1, alignItems: 'center' }}>
                <Button type="button" onClick={back}>Back</Button>
                <Button type="submit" variant="contained" disabled={codeBlocked}
                    startIcon={status.state === 'checking' ? <CircularProgress size={18} color="inherit" /> : undefined}>
                    Continue
                </Button>
            </Box>
        </Box>
    );
}
